import { useCallback, useEffect, useState } from "react";

import { animated, useSpring } from "@react-spring/web";
import HelpCircleIcon from "@uui/HelpCircle";
import HelpIcon from "@uui/HelpCircle";
import CloseIcon from "@uui/XClose";

import Fab from "@mui/material/Fab";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { hideZendeskWidget, openZendeskWidget } from "@app/common/zendeskUtils";
import Config from "@app/config";
import log from "@app/utils/logger";
import { loadScript } from "@app/utils/scripts";

const ZENDESK_URL = "https://static.zdassets.com/ekr/snippet.js";
const ZENDESK_ID = "ze-snippet";

const StyledWrapper = styled(Stack)({
  position: "fixed",
  right: "0px",
  top: "50%",
  zIndex: 999998, // same z-index as built-in zendesk widget
  width: "32px",
  transition: "background-color 0.1s ease-in-out, width 0.2s ease-in-out",
  "& .MuiIconButton-root": {
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.2s ease-in-out",
  },
  "&:hover": {
    width: "40px",
    "& .MuiIconButton-root": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
});

const StyledWidget = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.shades.primary[500],
  cursor: "pointer",
  color: "#fff",
  width: "100%",
  height: "94px",
  borderRadius: "8px 0 0 8px",
  transition: "background-color 0.1s ease-in-out, width 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.shades.primary[700],
  },
}));

const VerticalStack = styled(Stack)({
  transform: "translate(0px,30px) rotate(-90deg)",
});

const SupportText = styled(Typography)({
  fontSize: "0.875rem",
  lineHeight: "16px",
  fontWeight: 500,
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  width: "24px",
  height: "24px",
  color: "#fff",
  backgroundColor: theme.palette.shades.primary[500],
  "&:hover": {
    backgroundColor: theme.palette.shades.primary[700],
  },
  "& svg": {
    width: "16px",
    height: "16px",
  },
}));

const SupportButton = styled((props: IconButtonProps) => (
  <IconButton {...props}>
    <HelpIcon sx={{ fontSize: "16px" }} />
  </IconButton>
))(({ theme }) => ({
  background: theme.palette.shades.primary[50],
  borderRadius: theme.corners.round,
  padding: theme.spaces.medium,
  height: "36px",
  width: "36px",
  "& .MuiSvgIcon-root": {
    color: theme.palette.shades.primary[500],
  },
  "&:active": {
    background: theme.palette.shades.primary[150],
  },
}));

const AnimatedWrapper = animated(StyledWrapper);
const AnimatedFab = animated(Fab);

interface WidgetProps {
  showLauncher: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const SidebarWidget = ({
  showLauncher,
  open,
  onToggle,
  onClose,
}: WidgetProps) => {
  const wrapperStyle = useSpring({
    to: { right: !showLauncher || open ? -40 : 0 },
  });

  return (
    <AnimatedWrapper style={wrapperStyle} alignItems="center" spacing="4px">
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      <StyledWidget onClick={onToggle}>
        <Stack
          direction="column"
          sx={{ height: "100%", width: "32px", padding: "8px" }}
          justifyContent="center"
        >
          <VerticalStack direction="row" alignItems="center" spacing="4px">
            <HelpCircleIcon sx={{ width: "16px", height: "16px" }} />
            <SupportText>Support</SupportText>
          </VerticalStack>
        </Stack>
      </StyledWidget>
    </AnimatedWrapper>
  );
};

const FabWidget = ({ showLauncher, open, onToggle }: WidgetProps) => {
  const wrapperStyle = useSpring({
    to: { opacity: !showLauncher || open ? 0 : 1.0 },
  });

  // use MUI Fab to show a help widget in the bottom right of the screen that expands to say "support" when hovered over
  return (
    <AnimatedFab
      style={wrapperStyle}
      aria-label="support"
      onClick={onToggle}
      sx={{
        background: "#fff",
        position: "fixed",
        bottom: "16px",
        right: "16px",
        zIndex: 999999,
        width: "40px",
        height: "40px",
        borderRadius: "24px",
        padding: "12px",
        transition: "width 0.2s ease-in-out",
        "&:hover": {
          width: "112px",
          backgroundColor: "shades.ghost.500",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        sx={{ width: "100%", height: "100%", overflow: "hidden" }}
      >
        <Stack direction="row" alignItems="center" spacing="4px">
          <HelpCircleIcon
            sx={{ width: "16px", height: "16px", color: "shades.primary.500" }}
          />
          <Typography
            variant="bodyPrimaryMed"
            sx={{ color: "shades.primary.500" }}
          >
            Support
          </Typography>
        </Stack>
      </Stack>
    </AnimatedFab>
  );
};

const ButtonWidget = ({ onToggle }: WidgetProps) => {
  return <SupportButton onClick={onToggle} />;
};

const WIDGET_VARIANT_MAP = {
  bar: SidebarWidget,
  fab: FabWidget,
  btn: ButtonWidget,
};

const ZendeskWidget = ({
  variant = "bar",
}: {
  variant?: "bar" | "fab" | "btn";
}) => {
  const [showLauncher, setShowLauncher] = useLocalStorage(
    "showZendeskLauncher",
    true,
    { scope: "session", migrateKeys: true }
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggleWidget = useCallback((open: boolean) => {
    if (open) {
      openZendeskWidget();
      setIsOpen(true);
    } else {
      hideZendeskWidget();
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    const zendeskUrl = `${ZENDESK_URL}?key=${Config.zendesk.key}`;

    // Check if the script is already loaded
    if (window.zE && !isLoaded) {
      window.zE("webWidget:on", "close", () => {
        handleToggleWidget(false);
      });
      setIsLoaded(true);
    } else if (!isLoaded && !isLoading) {
      setIsLoading(true);
      loadScript({ src: zendeskUrl, id: ZENDESK_ID })
        .then(() => {
          hideZendeskWidget();
          window.zE("webWidget:on", "close", () => {
            handleToggleWidget(false);
          });
          setIsLoading(false);
          setIsLoaded(true);
        })
        .catch((error) => {
          setIsLoading(false);
          log.debug("unable to load zendesk widget", error);
        });
    }

    return () => hideZendeskWidget();
  }, [isLoaded, isLoading]);

  if (!isLoaded) {
    return null;
  }

  const Widget = WIDGET_VARIANT_MAP[variant];

  return (
    <Widget
      open={isOpen}
      showLauncher={showLauncher}
      onClose={() => setShowLauncher(false)}
      onToggle={() => handleToggleWidget(!isOpen)}
    />
  );
};

export default ZendeskWidget;
